import { useEffect, useRef, useState } from "react";
import { STATS } from "@/data/content";

function useCountUp(target, durationMs = 1600, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let rafId;
    const startTs = performance.now();
    const animate = (now) => {
      const t = Math.min(1, (now - startTs) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(t >= 1 ? target : Math.round(target * eased));
      if (t < 1) rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [target, durationMs, start]);
  return value;
}

function StatItem({ value, suffix, label, start, testId }) {
  const v = useCountUp(value, 1600, start);
  return (
    <div data-testid={testId} className="text-center lg:text-left lg:border-l lg:first:border-l-0 lg:pl-8 lg:first:pl-0">
      <div className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-none tracking-tight">
        {v}{suffix}
      </div>
      <div className="mt-3 text-sm uppercase tracking-[0.18em] text-blue-100/80 font-medium">{label}</div>
    </div>
  );
}

export default function Stats() {
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setActive(true); io.disconnect(); } },
      { threshold: 0.3 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="stats-section"
      className="relative py-20 sm:py-24 bg-slate-900 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-primary/30 to-slate-900" />
      <div className="absolute inset-0 bg-grid-hero opacity-30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
          {STATS.map((s, i) => (
            <StatItem key={s.label} {...s} start={active} testId={`stat-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
